import { Controller } from '@hotwired/stimulus'
import TomSelect from 'tom-select';

export default class extends Controller {
  // Define static value to fetch the URL from HTML data attributes.
  static values = {
    url: { type: String, default: null },
    options: { type: Array, default: [] },
    recordType: { type: String, default: "Company" },
    selected: { type: Array },
    singleItem: { type: Boolean, default: false },
    placeholder: { type: String, default: "Select an option" },
    companyId: Number,
    groupId: Number,
    autoFocus: { type: Boolean, default: false },
    assetType: { type: String, default: null },
    itemsOutside: { type: Boolean, default: false }
  };
  static targets = ["select", "companyButton", "globalButton", "itemsContainer"];

  // Triggered when the Stimulus controller is connected to the DOM.
  connect() {
    this.initializeTomSelect();
    this.searchGlobal = false;
    
    // Setup without tags listener for Tag record type
    if (this.recordTypeValue === 'Tag') {
      this.setupWithoutTagsListener();
    }
  }

  // Triggered when the Stimulus controller is removed from the DOM.
  disconnect() {
    this.destroyTomSelect();
    this.cleanupWithoutTagsListener();
  }

  setupWithoutTagsListener() {
    this.withoutTagsCheckbox = document.getElementById('filters__without_tags');
    if (this.withoutTagsCheckbox) {
      this.boundHandleWithoutTagsChange = this.handleWithoutTagsChange.bind(this);
      this.withoutTagsCheckbox.addEventListener('change', this.boundHandleWithoutTagsChange);
      
      // Check initial state
      this.handleWithoutTagsChange();
    }
  }

  cleanupWithoutTagsListener() {
    if (this.withoutTagsCheckbox && this.boundHandleWithoutTagsChange) {
      this.withoutTagsCheckbox.removeEventListener('change', this.boundHandleWithoutTagsChange);
    }
  }

  handleWithoutTagsChange() {
    if (this.withoutTagsCheckbox.checked) {
      // Clear all tags and disable the input
      if (this.select) {
        this.select.clear();
        this.select.disable();
      }
    } else {
      // Enable the input
      if (this.select) {
        this.select.enable();
      }
    }
  }


  // Sanitize CSS class names to prevent injection
  sanitizeIconClass(iconClass) {
    if (!iconClass) return '';
    // Only allow alphanumeric, hyphens, underscores, and spaces (for multiple classes)
    return String(iconClass).replace(/[^a-zA-Z0-9\s_-]/g, '');
  }

  changeToGlobal(event) {
    event.preventDefault();
    // Clear existing search results but keep selected items
    this.select.clearOptions();
    this.select.setTextboxValue('');

    this.searchGlobal = true;
    this.companyButtonTarget.classList.remove("selected");
    this.globalButtonTarget.classList.add("selected");

    this.select.trigger("load");
  }

  changeToCompany(event) {
    event.preventDefault();
    // Clear existing search results but keep selected items
    this.select.clearOptions();
    this.select.setTextboxValue('');

    this.searchGlobal = false;
    this.companyButtonTarget.classList.add("selected");
    this.globalButtonTarget.classList.remove("selected");

    this.select.trigger("load");
  }

  // Initialize the TomSelect dropdown with the desired configurations.
  initializeTomSelect() {
    // Return early if no element is associated with the controller.
    if (!this.element) return;

    const fetchData = this.urlValue ? this.fetchFromUrl : this.fetchFromOptions;

    const renderOption = (data, escape) => {
      if (data.color) {
        return `
          <div style="display: flex; align-items: center;">
            <div class='circle circle--flag circle--table circle--${data.color}'></div>
            <span>${escape(data.name)}</span>
          </div>
        `;
      } else if (data.company_name == null) {
        return `
          <div>
            <span>${escape(data.name)}</span>
          </div>
        `;
      } else {
        // Asset with company info - include icon
        const iconHtml = `<div class='asset-icon' style='color: ${escape(data.icon_color)}; background-color: ${escape(data.icon_background_color)};'>
            <i class='${this.sanitizeIconClass(data.icon)}'></i>
           </div>`;

        return `
          <div style="display: flex; align-items: center;">
            ${iconHtml}
            <div>
              <span>${escape(data.name)}</span>
              <span class='index__small'>${escape(data.asset_type)} / ${escape(data.company_name)}</span>
            </div>
          </div>
        `;
      }
    };

    var plugins = this.singleItemValue ? [] : ["remove_button"];

    // If items are outside, don't use remove_button plugin since we handle removal externally
    if (this.itemsOutsideValue && !this.singleItemValue) {
      plugins = [];
    }

    // Configure TomSelect options
    const tomSelectOptions = {
      plugins: plugins,
      valueField: 'id',
      labelField: 'name',
      searchField: ['name'],
      maxItems: this.singleItemValue ? 1 : null,
      placeholder: this.placeholderValue,
      preload: true,
      create: false,
      sortField: { field: "name", direction: "asc" },
      load: fetchData,
      onItemAdd: () => {
        this.select.setTextboxValue('');
        if (this.itemsOutsideValue && !this.singleItemValue) {
          this.renderExternalItems();
        }
        this.select.close();
      },
      onItemRemove: () => {
        this.select.setTextboxValue('');
        if (this.itemsOutsideValue && !this.singleItemValue) {
          this.renderExternalItems();
        }
        this.select.close();
      },
      render: {
        item: (data, escape) => {
          // If items are outside and it's multi-select, don't render items in the control
          if (this.itemsOutsideValue && !this.singleItemValue) {
            return '<div style="display: none;"></div>';
          }

          const option = this.optionsValue.find(option => option.id == data.id) || {};
          if (option.color) {
            return `
              <div data-hide-remove="${option.hide_remove || false}" style="display: flex; align-items: center;">
                <div class='circle circle--flag circle--table circle--${option.color}'></div>
                ${escape(data.name)}
              </div>
            `;
          } else {
            // Only render asset icon if there's actual icon data
            const hasIconData = data.icon || data.icon_color || data.icon_background_color;
            const iconHtml = hasIconData ? `<div class='asset-icon small' style='color: ${escape(data.icon_color)}; background-color: ${escape(data.icon_background_color)};'>
                <i class='${this.sanitizeIconClass(data.icon)}'></i>
               </div>` : '';

            return `
              <div data-hide-remove="${option.hide_remove || false}" style="display: flex; align-items: center;">
                ${iconHtml}
                <span>${escape(data.name)}</span>
              </div>
            `;
          }
        },
        option: renderOption
      },
    };

    this.select = new TomSelect(this.selectTarget, tomSelectOptions);

    if (this.selectedValue.length > 0) {
      this.selectedValue.forEach((value) => {
        this.select.addOption({
          id: value.id,
          name: value.name,
          company_name: value.company_name,
          asset_type: value.asset_type,
          icon: value.icon,
          icon_color: value.icon_color,
          icon_background_color: value.icon_background_color
        });
        this.select.addItem(value.id);
      });
    }

    // Initial render of external items if needed
    if (this.itemsOutsideValue && !this.singleItemValue) {
      this.renderExternalItems();
    }

    // Auto-focus the search input if auto_focus is enabled
    if (this.autoFocusValue) {
      // Use a small delay to ensure TomSelect is fully initialized
      setTimeout(() => {
        this.select.focus();
      }, 100);
    }
  }

  renderExternalItems() {
    if (!this.hasItemsContainerTarget) return;

    const selectedItems = this.select.items;
    const itemsHtml = selectedItems.map(itemId => {
      const option = this.select.options[itemId];
      if (!option) return '';

      const optionData = this.optionsValue.find(opt => opt.id == itemId) || {};

      let itemHtml = `<div class="external-item">`;

      if (optionData.color) {
        itemHtml += `<div class='circle circle--flag circle--table circle--${optionData.color}' style="margin-right: 6px;"></div>`;
      }

      itemHtml += `
          <span>${option.name}</span>
          <button type="button" class="remove-item" data-action="click->select#removeExternalItem" data-item-id="${itemId}">×</button>
        </div>
      `;

      return itemHtml;
    }).join('');

    this.itemsContainerTarget.innerHTML = itemsHtml;
  }

  removeExternalItem(event) {
    event.preventDefault();
    const itemId = event.currentTarget.dataset.itemId;
    this.select.removeItem(itemId);
  }

  fetchFromUrl = (search, callback) => {
    let params = new URLSearchParams({
      q: search,
      type: this.recordTypeValue,
      include_archived: "false"
    });

    if (this.companyIdValue && !this.searchGlobal) {
      params.append("company", this.companyIdValue);
    }

    if (this.hasGroupIdValue && this.groupIdValue) {
      params.append("group_id", this.groupIdValue);
    }

    if (this.hasAssetTypeValue && this.assetTypeValue) {
      params.append("asset_type", this.assetTypeValue);
    }

    fetch(`${this.urlValue}?${params.toString()}`)
      .then(response => response.json())
      .then(data => callback(data))
      .catch(() => callback());
  };

  fetchFromOptions = (search, callback) => {

    const filteredOptions = this.optionsValue.filter(option =>
      option.name.toLowerCase().includes(search.toLowerCase())
    );
    callback(filteredOptions);
  }

  // Cleanup: Destroy the TomSelect instance when the controller is disconnected.
  destroyTomSelect() {
    if (this.select) {
      this.select.destroy();
    }
  }
}
